import {createAsyncThunk} from '@reduxjs/toolkit';
import {MoodLightCategory, ProfileVersion} from 'puffco-api-axios-client';
import {ulid} from 'ulid';

import {Constants} from '../../../constants';
import {moodlightsApi} from '../../api/apis';
import {ReadHeatCycle} from '../../ble2/v2/PeakDevice/UnitCycleAccess';
import {Store} from '../../redux/types';
import {Device, HeatProfile, isColorLight, isMoodLightId} from '../../types';
import {getDeviceFromPeak} from '../../utils/getDeviceFromPeak';
import {isDefined} from '../../utils/types';
import {
  connectedPeakSelector,
  currentDeviceSelector,
  updateDevice,
  updateDeviceSettings,
} from '../slices/bleSlice';
import {
  upsertAccountMoodLights,
  upsertExclusiveMoodLights,
} from '../slices/moodLightSlice';
import {userSelector} from '../slices/userSlice';

const findMatchingHeatProfile =
  (knownProfiles: Record<string, HeatProfile>) =>
  (cycle: ReadHeatCycle): HeatProfile | undefined => {
    // For devices that do not support scratchpad (<T) the id is missing,
    // so we need to match the raw values with the known profiles
    if (!cycle.id) {
      const found = Object.values(knownProfiles).find(profile => {
        return (
          cycle.name === profile.name &&
          cycle.temperature === profile.temperature &&
          cycle.duration === profile.duration &&
          isColorLight(cycle.light) &&
          isColorLight(profile.light) &&
          cycle.light === profile.light
        );
      });

      return found;
    }

    return knownProfiles[cycle.id];
  };

export const toHeatProfile = (
  cycle: ReadHeatCycle,
  knownProfiles: Record<string, HeatProfile>,
) => {
  const heatProfile = findMatchingHeatProfile(knownProfiles)(cycle);

  if (!heatProfile) {
    const created = new Date().toISOString();

    // Create new heat profile from cycle
    return {
      ...cycle,
      id: cycle.id ?? ulid(),
      created,
      modified: created,
      version: ProfileVersion.T,
    };
  }

  return {...heatProfile, ...cycle};
};

export const bleReadDeviceSettings = createAsyncThunk<void>(
  'ble/readDeviceSettings',
  async (_, thunkAPI) => {
    const {dispatch, getState} = thunkAPI;
    const state = getState() as Store;
    const peak = connectedPeakSelector(state);
    const device = currentDeviceSelector(state);
    const knownProfiles = state.profile.items;
    const user = userSelector(state);

    if (!device || !peak) return;

    const attributes = await peak.readDeviceAttributes();
    const dabbingValues = await peak.readDabbingValues();

    const lanternMode = attributes.lanternTime > 0;
    const lantern = await peak.leds.readLight({target: 'lantern'});

    const heatCycles = await peak.cycles.readHeatCycles({
      units: state.appSettings.tempPreference,
    });
    const tempHeatCycle = await peak.cycles.readHeatCycle(
      Constants.TEMP_HEAT_PROFILE_INDEX,
      {
        units: state.appSettings.tempPreference,
      },
    );

    const heatProfiles = heatCycles.map<HeatProfile>(p =>
      toHeatProfile(p, knownProfiles),
    );
    const tempHeatProfile = toHeatProfile(tempHeatCycle, knownProfiles);

    const moodLightIds = Array.from(
      new Set(
        [...heatProfiles, tempHeatProfile]
          .map(hp => {
            if (isMoodLightId(hp.light)) return hp.light.id;

            return;
          })
          .filter(isDefined),
      ),
    );

    const moodLights =
      moodLightIds.length > 0
        ? await moodlightsApi
            .getMoodLights({
              size: 5,
              ids: moodLightIds,
            })
            .then(({data}) => data.items)
            .catch(() => [])
        : [];
    const moodLightsMap = Object.fromEntries(
      moodLights.map(item => [item.id, item]),
    );

    const deviceAttributes = getDeviceFromPeak(peak);

    const aggregatedHeatProfiles = heatProfiles.map(p => ({
      ...p,
      light: isMoodLightId(p.light)
        ? moodLightsMap[p.light.id] ?? p.light
        : p.light,
    }));
    const aggregatedTempHeatProfile = {
      ...tempHeatProfile,
      light: isMoodLightId(tempHeatProfile.light)
        ? moodLightsMap[tempHeatProfile.light.id]
        : tempHeatProfile.light,
    };

    const newDevice: Device = {
      ...device,
      ...deviceAttributes,
      heatProfiles: aggregatedHeatProfiles,
      tempHeatProfile: aggregatedTempHeatProfile,
      lantern: {on: lanternMode, light: lantern.light},
      settings: {
        ...dabbingValues.settings,
        ...deviceAttributes.settings,
        boostTemp: attributes.boostTemperature,
        boostDuration: attributes.boostTime,
        readyMode: attributes.mode >= 0 && attributes.mode < 4,
        readyProfile: attributes.mode < 4 ? attributes.mode : undefined,
        brightness: attributes.devBrightness[2],
      },
    };

    const accountMoodLights = moodLights.filter(
      item =>
        item.category === MoodLightCategory.Custom &&
        item.user?.id === user?.id,
    );
    const exclusiveMoodLights = moodLights.filter(
      ({category}) => category === MoodLightCategory.Exclusive,
    );

    if (accountMoodLights.length)
      dispatch(upsertAccountMoodLights(accountMoodLights));
    if (exclusiveMoodLights.length)
      dispatch(upsertExclusiveMoodLights(exclusiveMoodLights));

    dispatch(updateDevice(newDevice));
    dispatch(updateDeviceSettings(newDevice));
  },
);
